import withAdminRights from '@/components/Auth/withAdminRights';
import React, { useCallback, useMemo, useRef, useState, useEffect, useContext } from 'react';
import { compose } from 'recompose';
import { SEARCH_OPTIONS, useTabs } from './constants';
import STATES from '@/constants/states';
import { ADMIN_ORDERS } from './graphql';
import { captureException } from '@sentry/react';
import { NetworkStatus, useMutation, useQuery } from '@apollo/client';
import useAction from '@/utilities/useQuery';
import { PrimaryButton } from '@/styles/blocks';
import {
    Button,
    Grid,
    InputAdornment,
    LinearProgress,
    MenuItem,
    Snackbar,
    TextField,
    Typography,
} from '@material-ui/core';
import { css } from '@emotion/react';
import { Container, OuterContainer, Row, Title } from './blocks';
import { FilterList, Search } from '@material-ui/icons';
import { OnwardTab, OnwardTabContainer } from '@/components/Tabs';
import AdminOrdersColumnFilterPanel, { COLUMN_FILTER_MAP as ADMIN_COLUMN_FILTER_MAP } from './ColumnFilterPanel';
import AdminSettingsPanel from './AdminSettingsPanel';
import CustomizableTable from '@/components/CustomizableTable';
import { ACCOUNTING_COLUMNS, useColumns, useRowHighlight } from './constants';
import { Alert, Pagination } from '@material-ui/lab';
import { colors } from '@/styles';
import { asUTCDate } from '@/utilities/convertToISO';
import { addDays, addYears } from 'date-fns';
import EditOrderModal from '@/components/ShipmentForm/modals/EditOrderModal';
import { exportCsv } from './queries/exportCsv';
import { isObject } from 'lodash';
import { UPSERT_ORDERS } from '@/components/ShipmentForm/graphql/mutations';
import OrderPODModal from '@/components/CarrierAccountingOrders/modals/OrderPODModal';
import { EDIT_ORDER_TABS } from '@/components/ShipmentForm/constants';
import useShippersAndCarriers from '@/hooks/useShippersAndCarriers';
import add from 'date-fns/add';
import formatISO from 'date-fns/formatISO';
import { MILESTONE_OPTIONS } from '@onward-delivery/core';
import { MARKETPLACE_TIERS } from '@onward-delivery/core';
import { defaultTiers } from '@/components/admin/AdminPricing/constants';
import { isOrderDistressed, useOrderColor } from '@/components/Account/MilestoneThresholds/util';
import { UserContext } from '@/components/App';
import { useOrderTableProfiles } from '@/hooks';
import CustomizableTableProfileControl from '@/components/CustomizableTable/CustomizableTableProfileControl';
import { QUERY_ONWARD_ADMINS } from '@/graphql/queries/users';
import { withWarehouseEstimateEvent } from '@/utilities/warehouseEvents';

const TIER_OPTIONS = [
    { value: 'Unknown', filter: 'UNKNOWN' },
    { value: 'Tier 1 - Integrations Only', filter: MARKETPLACE_TIERS.T1_INTEGRATIONS_ONLY },
    { value: 'Tier 2 - Integrations Only', filter: MARKETPLACE_TIERS.T2_INTEGRATIONS_ONLY },
    { value: 'Tier 2 - Integrations & Billing', filter: MARKETPLACE_TIERS.T2_INTEGRATIONS_BILLING },
    { value: 'Tier 3 - Fully Managed', filter: MARKETPLACE_TIERS.T3_FULLY_MANAGED },
];

const DEFAULT_HIDDEN_COLUMNS = [
    'route',
    'customer_email',
    'origin',
    'destination',
    'posted_date',
    'claimed_date',
    'shipper_rate',
    'middle_mile_shipper_rate',
    'carrier_rate',
    'middle_mile_carrier_rate',
    'margin',
    'manufacturer',
    'middle_mile_integration_meta.tracking_number',
    'middle_mile_integration_type',
    'dispatch_integration_type',
    'dispatch_integration_meta.tracking_number',
    'warehousestatus',
    'rereceivingstatus',
    'loaded_at',
    'ofd_at',
    'arrived_at',
    'completed_at',
    'photos_present',
    'assigned_admin',
];

const DEFAULT_TABLE_INITIAL_STATE = {
    hiddenColumns: DEFAULT_HIDDEN_COLUMNS,
    columnOrder: [],
    columnResizing: { columnWidths: {} },
};

const AdminOrders = () => {
    const TODAY = new Date(new Date().setHours(0, 0, 0, 0));
    const handleColor = useOrderColor();
    const { user } = useContext(UserContext);

    // Misc State
    const [notification, setNotification] = useState({});
    const [editingOrder, editOrder] = useState(null);
    const [podOrder, viewOrderPod] = useState(null);
    const [openColumnFilter, setOpenColumnFilter] = useState(null);
    const [openAdminSettings, setOpenAdminSettings] = useState(false);

    // Profiles (CustomizableTable settings templates)
    const [tableInitialState, setTableInitialState] = useState(() => DEFAULT_TABLE_INITIAL_STATE);
    const [tableKey, setTableKey] = useState(0);
    const [tableLayoutState, setTableLayoutState] = useState({
        hiddenColumns: [],
        columnOrder: [],
        columnWidths: {},
    });

    const TABS = useTabs({ page: 'admin' }, { viewOrderPod });
    useEffect(() => {
        setTab(
            Math.max(
                TABS.findIndex((t) => t.value === 'ALL_ACTIVE'),
                0
            )
        );
    }, [TABS]);

    // Basic filters
    const [tab, setTab] = useState(0);
    const [search, setSearch] = useState('');
    const [searchField, setSearchField] = useState('order_number');
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncer, setDebouncer] = useState(null);
    const [test, setTest] = useState(false);

    // Advanced filters
    const [shipperIds, setShipperIds] = useState([]);
    const [carrierIds, setCarrierIds] = useState([]);
    const [states, setStates] = useState([]);
    const [deliveryTypes, setDeliveryTypes] = useState([]);
    const [startDate, setStartDate] = useState(new Date(addYears(TODAY, -1)).toISOString());
    const [endDate, setEndDate] = useState(null);
    const [completedStart, setCompletedStart] = useState(null);
    const [completedEnd, setCompletedEnd] = useState(null);
    const [paymentType, setPaymentType] = useState(null);
    const [marketplace, setMarketplace] = useState(true);
    const [assignedAdminId, setAssignedAdminId] = useState(null);

    const [sortBy, setSortBy] = useState({ id: 'created_at', desc: true });
    const [middleMileStatus, setMiddleMileStatus] = useState(null);
    const [warehouseStatus, setWarehouseStatus] = useState(null);
    const [rereceivingStatus, setRereceivingStatus] = useState(null);
    const [orderType, setOrderType] = useState(null);
    const [orderKinds, setOrderKinds] = useState([]);
    const [schedDelStart, setSchedDelStart] = useState(null);
    const [schedDelEnd, setSchedDelEnd] = useState(null);
    const [estShipStart, setEstShipStart] = useState(null);
    const [estShipEnd, setEstShipEnd] = useState(null);
    const [actualReceivedStart, setActualReceivedStart] = useState(null);
    const [actualReceivedEnd, setActualReceivedEnd] = useState(null);
    const [warehouseStartDate, setWarehouseStartDate] = useState(null);
    const [warehouseEndDate, setWarehouseEndDate] = useState(null);
    const [milestone, setMilestone] = useState(null);
    const [tiers, setTiers] = useState(defaultTiers);
    const filterableColumnIds = useMemo(
        () => [
            'shipper',
            'carrier',
            'assigned_admin',
            'tier',
            'delivery_type',
            'posted_date',
            'delivery_date',
            'est_ship_date',
            'est_received_date',
            'received_date',
            'warehousestatus',
            'rereceivingstatus',
            'middle_mile_integration_status',
            'order_type',
            'order_kind',
            'milestone',
            'payment_type',
        ],
        []
    );

    const { data: onwardAdminsData } = useQuery(QUERY_ONWARD_ADMINS);
    const onwardAdmins = useMemo(() => onwardAdminsData?.users || [], [onwardAdminsData]);
    const onwardAdminsById = useMemo(
        () =>
            Object.fromEntries(
                onwardAdmins.map((u) => [u.user_id, u.username || u.email || u.user_id]).filter(([id]) => !!id)
            ),
        [onwardAdmins]
    );
    const onwardAdminOptions = useMemo(
        () => [
            { value: '', label: 'All' },
            ...onwardAdmins.map((u) => ({
                value: u.user_id,
                label: u.username || u.email || u.user_id,
            })),
        ],
        [onwardAdmins]
    );

    const buildProfileSettings = useCallback(() => {
        return {
            layout: {
                hiddenColumns: tableLayoutState.hiddenColumns || [],
                columnOrder: tableLayoutState.columnOrder || [],
                columnWidths: tableLayoutState.columnWidths || {},
            },
            sorting: {
                sortBy: sortBy?.id ? [{ id: sortBy.id, desc: !!sortBy.desc }] : [],
            },
            filters: {
                values: {
                    marketplace,
                    test,
                    shipperIds,
                    carrierIds,
                    tiers,
                    deliveryTypes,
                    startDate,
                    endDate,
                    completedStart,
                    completedEnd,
                    schedDelStart,
                    schedDelEnd,
                    estShipStart,
                    estShipEnd,
                    actualReceivedStart,
                    actualReceivedEnd,
                    warehouseStartDate,
                    warehouseEndDate,
                    warehouseStatus,
                    rereceivingStatus,
                    middleMileStatus,
                    orderType,
                    orderKinds,
                    milestone,
                    paymentType,
                    assignedAdminId,
                    states,
                },
            },
        };
    }, [
        tableLayoutState,
        sortBy,
        marketplace,
        test,
        shipperIds,
        carrierIds,
        tiers,
        deliveryTypes,
        startDate,
        endDate,
        completedStart,
        completedEnd,
        schedDelStart,
        schedDelEnd,
        estShipStart,
        estShipEnd,
        actualReceivedStart,
        actualReceivedEnd,
        warehouseStartDate,
        warehouseEndDate,
        warehouseStatus,
        rereceivingStatus,
        middleMileStatus,
        orderType,
        orderKinds,
        milestone,
        paymentType,
        assignedAdminId,
        states,
    ]);

    const applyProfileSettings = useCallback(
        (settings = {}) => {
            const nextFilters = settings?.filters?.values || {};

            if (Object.prototype.hasOwnProperty.call(nextFilters, 'marketplace'))
                setMarketplace(!!nextFilters.marketplace);
            if (Object.prototype.hasOwnProperty.call(nextFilters, 'test')) setTest(!!nextFilters.test);

            if (Object.prototype.hasOwnProperty.call(nextFilters, 'shipperIds'))
                setShipperIds(nextFilters.shipperIds || []);
            if (Object.prototype.hasOwnProperty.call(nextFilters, 'carrierIds'))
                setCarrierIds(nextFilters.carrierIds || []);
            if (Object.prototype.hasOwnProperty.call(nextFilters, 'tiers')) setTiers(nextFilters.tiers || defaultTiers);
            if (Object.prototype.hasOwnProperty.call(nextFilters, 'deliveryTypes'))
                setDeliveryTypes(nextFilters.deliveryTypes || []);
            if (Object.prototype.hasOwnProperty.call(nextFilters, 'states')) setStates(nextFilters.states || []);

            if (Object.prototype.hasOwnProperty.call(nextFilters, 'startDate'))
                setStartDate(nextFilters.startDate || new Date(addYears(TODAY, -1)).toISOString());
            if (Object.prototype.hasOwnProperty.call(nextFilters, 'endDate')) setEndDate(nextFilters.endDate || null);
            if (Object.prototype.hasOwnProperty.call(nextFilters, 'completedStart'))
                setCompletedStart(nextFilters.completedStart || null);
            if (Object.prototype.hasOwnProperty.call(nextFilters, 'completedEnd'))
                setCompletedEnd(nextFilters.completedEnd || null);
            if (Object.prototype.hasOwnProperty.call(nextFilters, 'schedDelStart'))
                setSchedDelStart(nextFilters.schedDelStart || null);
            if (Object.prototype.hasOwnProperty.call(nextFilters, 'schedDelEnd'))
                setSchedDelEnd(nextFilters.schedDelEnd || null);
            if (Object.prototype.hasOwnProperty.call(nextFilters, 'estShipStart'))
                setEstShipStart(nextFilters.estShipStart || null);
            if (Object.prototype.hasOwnProperty.call(nextFilters, 'estShipEnd'))
                setEstShipEnd(nextFilters.estShipEnd || null);
            if (Object.prototype.hasOwnProperty.call(nextFilters, 'actualReceivedStart'))
                setActualReceivedStart(nextFilters.actualReceivedStart || null);
            if (Object.prototype.hasOwnProperty.call(nextFilters, 'actualReceivedEnd'))
                setActualReceivedEnd(nextFilters.actualReceivedEnd || null);
            if (Object.prototype.hasOwnProperty.call(nextFilters, 'warehouseStartDate'))
                setWarehouseStartDate(nextFilters.warehouseStartDate || null);
            if (Object.prototype.hasOwnProperty.call(nextFilters, 'warehouseEndDate'))
                setWarehouseEndDate(nextFilters.warehouseEndDate || null);

            if (Object.prototype.hasOwnProperty.call(nextFilters, 'warehouseStatus'))
                setWarehouseStatus(nextFilters.warehouseStatus || null);
            if (Object.prototype.hasOwnProperty.call(nextFilters, 'rereceivingStatus'))
                setRereceivingStatus(nextFilters.rereceivingStatus || null);
            if (Object.prototype.hasOwnProperty.call(nextFilters, 'middleMileStatus'))
                setMiddleMileStatus(nextFilters.middleMileStatus || null);
            if (Object.prototype.hasOwnProperty.call(nextFilters, 'orderType'))
                setOrderType(nextFilters.orderType || null);
            if (Object.prototype.hasOwnProperty.call(nextFilters, 'orderKinds'))
                setOrderKinds(nextFilters.orderKinds || []);
            if (Object.prototype.hasOwnProperty.call(nextFilters, 'milestone'))
                setMilestone(nextFilters.milestone || null);
            if (Object.prototype.hasOwnProperty.call(nextFilters, 'paymentType'))
                setPaymentType(nextFilters.paymentType || null);
            if (Object.prototype.hasOwnProperty.call(nextFilters, 'assignedAdminId'))
                setAssignedAdminId(nextFilters.assignedAdminId || null);

            const sortArr = settings?.sorting?.sortBy;
            if (Array.isArray(sortArr) && sortArr[0]?.id) {
                setSortBy({ id: sortArr[0].id, desc: !!sortArr[0].desc });
            }

            const layout = settings?.layout || {};
            const initial = {
                hiddenColumns: layout.hiddenColumns || [],
                columnOrder: layout.columnOrder || [],
                columnResizing: { columnWidths: layout.columnWidths || {} },
            };
            setTableInitialState(initial);
            setTableKey((k) => k + 1); // re-mount CustomizableTable so initialState is applied
        },
        [TODAY]
    );

    const resetAdminOrdersToDefaults = useCallback(() => {
        // Reset admin settings + filters to defaults
        setMarketplace(true);
        setTest(false);
        setShipperIds([]);
        setCarrierIds([]);
        setStates([]);
        setDeliveryTypes([]);
        setTiers(defaultTiers);
        setPaymentType(null);
        setAssignedAdminId(null);
        setMiddleMileStatus(null);
        setWarehouseStatus(null);
        setRereceivingStatus(null);
        setOrderType(null);
        setOrderKinds([]);
        setMilestone(null);
        setSchedDelStart(null);
        setSchedDelEnd(null);
        setEstShipStart(null);
        setEstShipEnd(null);
        setActualReceivedStart(null);
        setActualReceivedEnd(null);
        setWarehouseStartDate(null);
        setWarehouseEndDate(null);
        setCompletedStart(null);
        setCompletedEnd(null);

        setStartDate(new Date(addYears(TODAY, -1)).toISOString());
        setEndDate(null);

        setSortBy({ id: 'created_at', desc: true });

        // Reset table layout to default hidden columns (and default widths/order)
        setTableInitialState(DEFAULT_TABLE_INITIAL_STATE);
        setTableKey((k) => k + 1);
    }, [TODAY]);

    const {
        profiles,
        profilesLoading,
        selectedProfileId,
        currentProfileName,
        currentProfileId,
        profileSelectProps,
        resetToDefaultProfile,
        handleSaveNewProfile,
        handleOverwriteProfile,
        handleDeleteCurrentProfile,
        handleImportProfileById,
        insertProfileLoading,
        updateProfileLoading,
        deleteProfileLoading,
    } = useOrderTableProfiles({
        tableType: 'ADMIN_ORDERS',
        userId: user?.user_id,
        buildProfileSettings,
        applyProfileSettings,
        onResetToDefault: resetAdminOrdersToDefaults,
        notifyError: (message) => setNotification({ severity: 'error', message }),
    });

    const addOneDay = (date) => {
        const alteredDate = add(new Date(date), { days: 2 });
        return formatISO(alteredDate, { representation: 'date' });
    };

    const [retrieveCsv, { loading: csvInflight }] = useAction(exportCsv, {
        onError: (err) => {
            captureException(err);
        },
    });

    useEffect(() => {
        if (marketplace) {
            setTiers(defaultTiers);
        } else {
            setTiers([]);
        }
    }, [marketplace]);

    useEffect(() => {
        setOpenColumnFilter(null);
    }, [tab]);

    const handleOpenColumnFilter = useCallback(
        (columnId) => {
            if (columnId === 'payment_type' && TABS?.[tab]?.value !== 'FINANCIALS') {
                return;
            }
            if (filterableColumnIds.includes(columnId)) {
                setOpenColumnFilter(columnId);
            }
        },
        [filterableColumnIds, tab, TABS]
    );

    const handleCloseColumnFilter = useCallback(() => setOpenColumnFilter(null), []);

    const handleClearColumnFilter = useCallback(
        (columnId) => {
            const config = ADMIN_COLUMN_FILTER_MAP[columnId];
            if (!config?.controls?.length) return;

            const applyReset = (key) => {
                switch (key) {
                    case 'shipperIds':
                        setShipperIds([]);
                        break;
                    case 'carrierIds':
                        setCarrierIds([]);
                        break;
                    case 'tiers':
                        setTiers(defaultTiers);
                        break;
                    case 'deliveryTypes':
                        setDeliveryTypes([]);
                        break;
                    case 'startDate':
                        setStartDate(new Date(addYears(TODAY, -1)).toISOString());
                        break;
                    case 'endDate':
                        setEndDate(null);
                        break;
                    case 'schedDelStart':
                        setSchedDelStart(null);
                        break;
                    case 'schedDelEnd':
                        setSchedDelEnd(null);
                        break;
                    case 'estShipStart':
                        setEstShipStart(null);
                        break;
                    case 'estShipEnd':
                        setEstShipEnd(null);
                        break;
                    case 'actualReceivedStart':
                        setActualReceivedStart(null);
                        break;
                    case 'actualReceivedEnd':
                        setActualReceivedEnd(null);
                        break;
                    case 'warehouseStartDate':
                        setWarehouseStartDate(null);
                        break;
                    case 'warehouseEndDate':
                        setWarehouseEndDate(null);
                        break;
                    case 'warehouseStatus':
                        setWarehouseStatus(null);
                        break;
                    case 'middleMileStatus':
                        setMiddleMileStatus(null);
                        break;
                    case 'orderType':
                        setOrderType(null);
                        break;
                    case 'orderKinds':
                        setOrderKinds([]);
                        break;
                    case 'milestone':
                        setMilestone(null);
                        break;
                    case 'paymentType':
                        setPaymentType(null);
                        break;
                    case 'assignedAdminId':
                        setAssignedAdminId(null);
                        break;
                    default:
                        break;
                }
            };

            config.controls.forEach((control) => {
                if (control.key) {
                    applyReset(control.key);
                }
                if (control.keys) {
                    control.keys.forEach(applyReset);
                }
            });

            if (openColumnFilter === columnId) {
                setOpenColumnFilter(null);
            }
        },
        [
            openColumnFilter,
            setShipperIds,
            setCarrierIds,
            setTiers,
            setDeliveryTypes,
            setStartDate,
            setEndDate,
            setSchedDelStart,
            setSchedDelEnd,
            setEstShipStart,
            setEstShipEnd,
            setActualReceivedStart,
            setActualReceivedEnd,
            setWarehouseStartDate,
            setWarehouseEndDate,
            setWarehouseStatus,
            setMiddleMileStatus,
            setOrderType,
            setMilestone,
            setPaymentType,
            setAssignedAdminId,
            setOrderKinds,
            TODAY,
        ]
    );

    // Build where gql
    const where = useMemo(() => {
        let filters = [...TABS[tab].filters];

        if (milestone) {
            const milestoneFilter = MILESTONE_OPTIONS.find((o) => o.value === milestone)?.filter;
            if (milestoneFilter) {
                filters.push(milestoneFilter);
            }
        }

        if (tiers?.length) {
            const tierFilters = TIER_OPTIONS.filter((t) => tiers.includes(t.value)).map((t) => t.filter);
            const hasUnknown = tiers.includes('Unknown');

            if (tierFilters.length) {
                if (hasUnknown) {
                    filters.push({
                        _or: [
                            {
                                shipper_rate_override: {
                                    marketplace_tier: { _in: tierFilters },
                                },
                            },
                            { shipper_rate_override_id: { _is_null: true } },
                        ],
                    });
                } else {
                    filters.push({
                        shipper_rate_override: {
                            marketplace_tier: { _in: tierFilters },
                        },
                    });
                }
            }
        }

        if (states?.length) {
            filters.push({
                _or: ['pickup_state', 'dropoff_state'].map((field) => ({
                    [field]: {
                        _in: [...states, ...states.map((stateCode) => STATES[stateCode])],
                    },
                })),
            });
        }

        if (marketplace) {
            filters.push({ oms: { _eq: false } });
        } else {
            filters.push({ oms: { _eq: true } });
        }

        if (searchTerm && searchField) {
            const searchOption = SEARCH_OPTIONS.find((option) => option.value === searchField);
            const fieldValue = searchOption?.parser ? searchOption.parser(searchTerm) : searchTerm;
            filters.push({
                _or: [
                    { [searchField]: { _ilike: `%${fieldValue}%` } },
                    { references: { value: { _ilike: `%${searchTerm}%` } } },
                ],
            });
        }
        if (test) {
            filters.push({ order_shipper: { test_acc: { _eq: true } } });
        } else {
            filters.push({
                _or: [
                    { order_shipper: { test_acc: { _is_null: true } } },
                    { order_shipper: { test_acc: { _eq: false } } },
                ],
            });
        }
        if (shipperIds.length > 0) {
            filters.push({ shipper_id: { _in: shipperIds } });
        }
        if (carrierIds.length > 0) {
            filters.push({ carrier_id: { _in: carrierIds } });
        }
        if (assignedAdminId) {
            filters.push({ assigned_admin_id: { _eq: assignedAdminId } });
        }
        if (startDate) {
            const startCutoff = asUTCDate(startDate);
            filters.push({ created_at: { _gte: startCutoff } });
        }
        if (endDate) {
            const endCutoff = addDays(asUTCDate(endDate), 1);
            filters.push({ created_at: { _lt: endCutoff } });
        }
        if (completedStart) {
            const startCutoff = asUTCDate(completedStart);
            filters.push({ completion_time: { _gte: startCutoff } });
        }
        if (completedEnd) {
            const endCutoff = addDays(asUTCDate(completedEnd), 1);
            filters.push({ completion_time: { _lt: endCutoff } });
        }

        if (schedDelStart) {
            filters.push({ delivery_date: { _gte: schedDelStart } });
        }

        if (schedDelEnd) {
            filters.push({ delivery_date: { _lt: addOneDay(schedDelEnd) } });
        }

        if (estShipStart) {
            filters.push({
                wh_events: {
                    _and: [{ action: { _eq: 'START:AWAITING' } }, { est_ship_date: { _gte: estShipStart } }],
                },
            });
        }

        if (estShipEnd) {
            filters.push({
                wh_events: {
                    _and: [{ action: { _eq: 'START:AWAITING' } }, { est_ship_date: { _lte: addOneDay(estShipEnd) } }],
                },
            });
        }

        if (actualReceivedStart) {
            filters.push({
                wh_events: {
                    _and: [
                        { transition: { next: { _eq: 'RECEIVING' } } },
                        { received_date: { _gte: actualReceivedStart } },
                    ],
                },
            });
        }

        if (actualReceivedEnd) {
            filters.push({
                wh_events: {
                    _and: [
                        { transition: { next: { _eq: 'RECEIVING' } } },
                        { received_date: { _lte: addOneDay(actualReceivedEnd) } },
                    ],
                },
            });
        }

        if (warehouseStartDate) {
            filters.push({
                wh_events: {
                    _and: [{ est_received_date: { _gte: warehouseStartDate } }],
                },
            });
        }

        if (warehouseEndDate) {
            filters.push({
                wh_events: {
                    _and: [{ est_received_date: { _lte: addOneDay(warehouseEndDate) } }],
                },
            });
        }

        if (middleMileStatus) {
            filters.push({
                middle_mile_integration_status: { _eq: middleMileStatus },
            });
        }

        if (warehouseStatus === 'RECEIVED') {
            filters.push({
                _or: [
                    { warehouse_status: { _eq: 'RECEIVED' } },
                    { wh_events: { status: { _eq: 'RECEIVED' } } },
                    { wh_events: { received_date: { _is_null: false } } },
                    { wh_events: { transition: { next: { _eq: 'RECEIVING' } } } },
                ],
            });
        } else if (warehouseStatus === 'NOT_DELIVERED') {
            filters.push({
                _and: [
                    { _not: { warehouse_status: { _eq: 'RECEIVED' } } },
                    { _not: { wh_events: { status: { _eq: 'RECEIVED' } } } },
                    { _not: { wh_events: { received_date: { _is_null: false } } } },
                    { _not: { wh_events: { transition: { next: { _eq: 'RECEIVING' } } } } },
                ],
            });
        }

        if (rereceivingStatus === 'RECEIVED') {
            filters.push({
                wh_events: { status: { _eq: 'RECEIVED' } },
            });
        } else if (rereceivingStatus === 'NOT_DELIVERED') {
            filters.push({
                _not: { wh_events: { status: { _eq: 'RECEIVED' } } },
            });
        }

        // Warehouse inventory receipts have no consignee. They stay out of OMS until a
        // delivery is created from that stock with a customer name and address. Explicit
        // Order Type = Inventory still returns them for ops.
        if (orderType) {
            filters.push({ order_type: { _eq: orderType } });
        } else {
            filters.push({
                _or: [{ order_type: { _is_null: true } }, { order_type: { _neq: 'wh_inventory' } }],
            });
        }

        if (orderKinds?.length) {
            const awaitingOrReceivingActions = ['START:AWAITING', 'START:RECEIVING'];

            const mmAndDelivery = {
                _and: [{ priced_as_middle_mile: { _eq: true } }, { has_middle_mile_origin: { _eq: true } }],
            };
            const receivingAndDelivery = {
                wh_events: { action: { _in: awaitingOrReceivingActions } },
            };
            const pickupAndDelivery = {
                _and: [
                    { _not: mmAndDelivery },
                    { _not: { wh_events: { action: { _in: awaitingOrReceivingActions } } } },
                ],
            };

            const selected = [];
            if (orderKinds.includes('MM + Delivery')) selected.push(mmAndDelivery);
            if (orderKinds.includes('Receiving + Delivery')) selected.push(receivingAndDelivery);
            if (orderKinds.includes('Pickup + Delivery')) selected.push(pickupAndDelivery);

            if (selected.length) {
                filters.push({ _or: selected });
            }
        }

        if (paymentType) {
            if (paymentType === 'Invoice') {
                filters.push({ listing: { payment_intent_id: { _is_null: true } } });
            } else {
                filters.push({ listing: { payment_intent_id: { _is_null: false } } });
            }
        }

        if (states?.length) {
            filters.push({
                _or: ['pickup_state', 'dropoff_state'].map((field) => ({
                    [field]: {
                        _in: [...states, ...states.map((stateCode) => STATES[stateCode])],
                    },
                })),
            });
        }
        if (deliveryTypes?.length) {
            filters.push({
                _or: ['dropoff_location_type', 'pickup_location_type'].map((field) => ({
                    [field]: {
                        _in: deliveryTypes,
                    },
                })),
            });
        }
        return {
            _and: [
                ...filters,
                { created_at: { _gt: '2023-06-01T00:00:00.000Z' } },
                {
                    _or: [
                        { shipper_id: { _nin: ['ecf513c1-aa84-454d-b5e2-7a216a229244'] } },
                        {
                            _and: [
                                { shipper_id: { _eq: 'ecf513c1-aa84-454d-b5e2-7a216a229244' } },
                                { created_at: { _gte: '2025-11-25T00:00:00.000Z' } },
                            ],
                        },
                    ], //ABC warehouse has over 53k older orders, we have started working with them again, only show newer orders on admin load board
                },
            ],
        };
    }, [
        tab,
        searchTerm,
        test,
        shipperIds,
        carrierIds,
        startDate,
        endDate,
        states,
        deliveryTypes,
        completedEnd,
        completedStart,
        paymentType,
        assignedAdminId,
        schedDelStart,
        schedDelEnd,
        warehouseEndDate,
        warehouseStartDate,
        estShipStart,
        estShipEnd,
        actualReceivedStart,
        actualReceivedEnd,
        middleMileStatus,
        warehouseStatus,
        orderType,
        orderKinds,
        marketplace,
        milestone,
        searchField,
        tiers,
    ]);

    const order_by = useMemo(() => {
        const ascOrDesc = sortBy?.desc ? 'desc_nulls_last' : 'asc_nulls_last';
        switch (sortBy?.id) {
            case 'created_at':
                return [{ created_at: ascOrDesc }, { order_number: 'desc_nulls_last' }];
            case 'delivery_date':
                return [{ delivery_date: ascOrDesc }, { order_number: 'desc_nulls_last' }];
            case 'delivery_date_scheduled_at':
                return [{ delivery_date_scheduled_at: ascOrDesc }, { order_number: 'desc_nulls_last' }];
            case 'posted_date':
                return [{ posted_date: ascOrDesc }, { order_number: 'desc_nulls_last' }];
            case 'last_modified_at':
                return [{ last_modified_at: ascOrDesc }, { order_number: 'desc_nulls_last' }];
            case 'shipper':
                return [{ order_shipper: { business_name: ascOrDesc } }, { order_number: 'desc_nulls_last' }];
            case 'carrier':
                return [{ order_carrier: { business_name: ascOrDesc } }, { order_number: 'desc_nulls_last' }];
            case 'delivery_type':
                return [{ dropoff_location_type: ascOrDesc }, { order_number: 'desc_nulls_last' }];
            case 'est_ship_date':
                return [{ est_ship_date: ascOrDesc }, { order_number: 'desc_nulls_last' }];
            case 'est_received_date':
                return [{ est_received_date: ascOrDesc }, { order_number: 'desc_nulls_last' }];
            case 'received_date':
                return [{ received_date: ascOrDesc }, { order_number: 'desc_nulls_last' }];
            case 'order_number':
                return [{ order_number: ascOrDesc }, { created_at: 'desc_nulls_last' }];
            case 'po_number':
                return [{ po_number: ascOrDesc }, { order_number: 'desc_nulls_last' }];
            default:
                return [{ delivery_date: 'desc_nulls_last' }, { order_number: 'desc_nulls_last' }];
        }
    }, [sortBy]);

    // Infinite scroll batch size
    const pageSize = 40;

    // Queries
    const activeTabValue = TABS?.[tab]?.value;
    const isDistressedTab = activeTabValue === 'DISTRESSED_SHIPMENTS';
    const requiresShipperSelection = isDistressedTab;
    const missingRequiredShipper = requiresShipperSelection && shipperIds.length === 0;

    const {
        loading: queryLoading,
        data,
        error,
        fetchMore,
        networkStatus,
    } = useQuery(ADMIN_ORDERS, {
        variables: {
            where,
            order_by,
            offset: 0,
            limit: pageSize,
        },
        skip: missingRequiredShipper,
        notifyOnNetworkStatusChange: true,
        onError: (error) => {
            captureException(error);
            console.error(error);
            setNotification({
                severity: 'error',
                message: error.message,
            });
        },
    });

    const exportCSV = useCallback(() => {
        return retrieveCsv({ query: where }).then((result) => {
            const TODAY = new Date();
            const blob = new Blob([result.data]);

            const filename = `Orders ${TODAY.getTime()}.csv`;
            const autoclick = document.createElement('a');
            const payload = URL.createObjectURL(blob);
            autoclick.setAttribute('href', payload);
            autoclick.setAttribute('download', filename);
            autoclick.style.visibility = 'hidden';
            document.body.appendChild(autoclick);
            autoclick.click();
            document.body.removeChild(autoclick);
        });
    }, [where, retrieveCsv]);

    const [allLoadedOrders, setAllLoadedOrders] = useState([]);
    const lastOffsetRef = useRef(0);
    const [fetchMoreLoading, setFetchMoreLoading] = useState(false);

    useEffect(() => {
        lastOffsetRef.current = 0;
        setFetchMoreLoading(false);
        setAllLoadedOrders([]);
    }, [where, order_by, missingRequiredShipper]);

    useEffect(() => {
        if (missingRequiredShipper) return;
        if (!data?.orders) return;
        setAllLoadedOrders(data.orders);
        lastOffsetRef.current = data.orders.length;
    }, [data?.orders, missingRequiredShipper]);

    const orders = allLoadedOrders;
    const totalCount = useMemo(
        () => (missingRequiredShipper ? 0 : data?.orders_aggregate?.aggregate?.count || 0),
        [data, missingRequiredShipper]
    );
    const displayedOrders = useMemo(() => {
        if (!isDistressedTab) return orders;
        return orders.filter((order) => isOrderDistressed({ order }));
    }, [isDistressedTab, orders]);

    const columnsForTab = useColumns(TABS[tab]?.value, { viewOrderPod }, { onwardAdminsById });
    const rowHighlightFn = useRowHighlight(TABS[tab]);

    const columns = useMemo(() => {
        return TABS[tab]?.value === 'FINANCIALS' ? ACCOUNTING_COLUMNS : columnsForTab;
    }, [TABS, tab, columnsForTab]);

    const [shippers, carriers] = useShippersAndCarriers(test);

    const handleSearch = (event) => {
        const input = event.target.value;
        setSearch(input);
        if (debouncer) {
            clearTimeout(debouncer);
        }
        setDebouncer(
            setTimeout(() => {
                setSearchTerm(input);
            }, 300)
        );
    };

    const handleSortBy = (newSortBy) => {
        if (newSortBy?.id !== sortBy.id || newSortBy.desc !== sortBy.desc) {
            setSortBy(newSortBy);
        }
    };

    const [submitOrder, { loading: submissionLoading }] = useMutation(UPSERT_ORDERS, {
        onError: (e) => {
            setNotification({ severity: 'error', message: 'Failed to update order. Please try again.' });
            captureException(e);
        },
        refetchQueries: [ADMIN_ORDERS],
        awaitRefetchQueries: true,
    });

    const onSave = useCallback(
        ({
            errors,
            order: modified,
            items: itemsModified,
            itemsRemoved,
            itemUpdates,
            tags,
            tagsRemoved,
            documents,
            orderIds,
            subscriptions,
            services,
            servicesRemoved,
        }) => {
            if (errors?.hasMissingField?.length) {
                setNotification({
                    severity: 'error',
                    message: `Order has missing fields: ${errors.hasMissingField.join(', ')}.`,
                });
            } else if (errors?.phoneInvalid) {
                setNotification({
                    severity: 'error',
                    message: 'Order has an invalid phone number.',
                });
            } else {
                if (errors?.failedPricing?.length) {
                    let message = `Pricing not calculated for ${modified.order_number}.`;
                    if (errors.failedPricing.includes('miles')) {
                        message = `${message} Cannot find distance between pickup and dropoff locations.`;
                    }
                    if (errors.failedPricing.includes('delivery_type')) {
                        message = `${message} ${
                            modified.order_type === 'return' ? 'Return Pickup' : 'Delivery'
                        } type not set.`;
                    }
                    setNotification({ severity: 'warning', message });
                }

                const { completion_date_formatted, billing_completion_date_formatted, notes, ...rest } = modified;

                submitOrder({
                    variables: {
                        orders: [
                            {
                                ...withWarehouseEstimateEvent(rest, editingOrder),
                                notes: {
                                    data: notes,
                                    on_conflict: {
                                        constraint: 'notes_pkey',
                                        update_columns: ['note'],
                                    },
                                },
                            },
                        ],
                        items: itemsModified,
                        removals: itemsRemoved,
                        item_updates: itemUpdates || [],
                        tags,
                        tag_removals: tagsRemoved,
                        order_ids: orderIds,
                        documents: documents,
                        email_subscriptions: subscriptions || [],
                        services: services || [],
                        service_removals: servicesRemoved || [],
                    },
                });
                editOrder(null);
            }
        },
        [editingOrder]
    );

    const initialLoading = queryLoading && allLoadedOrders.length === 0;
    const loading = useMemo(() => submissionLoading || initialLoading, [submissionLoading, initialLoading]);

    const hasMore = useMemo(() => allLoadedOrders.length < totalCount, [allLoadedOrders.length, totalCount]);
    const fetchMoreInFlightRef = useRef(false);

    const loadMoreOrders = useCallback(() => {
        if (missingRequiredShipper) return;
        if (!hasMore || fetchMoreInFlightRef.current) return;

        fetchMoreInFlightRef.current = true;
        setFetchMoreLoading(true);
        const currentOffset = lastOffsetRef.current;

        const timeoutId = setTimeout(() => {
            if (fetchMoreInFlightRef.current) {
                fetchMoreInFlightRef.current = false;
                setFetchMoreLoading(false);
                setNotification({
                    severity: 'warning',
                    message: 'Loading more orders is taking longer than expected. Please try again or refresh.',
                });
            }
        }, 30000);

        fetchMore({
            variables: {
                where,
                order_by,
                offset: currentOffset,
                limit: pageSize,
            },
        })
            .then((result) => {
                const newOrders = result?.data?.orders || [];
                if (newOrders.length > 0) {
                    setAllLoadedOrders((prev) => [...prev, ...newOrders]);
                    lastOffsetRef.current = currentOffset + newOrders.length;
                }
            })
            .catch((e) => {
                captureException(e);
                console.error(e);
                setNotification({
                    severity: 'error',
                    message: 'Failed to load more orders. Please refresh and try again.',
                });
            })
            .finally(() => {
                clearTimeout(timeoutId);
                fetchMoreInFlightRef.current = false;
                setFetchMoreLoading(false);
            });
    }, [missingRequiredShipper, hasMore, fetchMore, where, order_by, pageSize]);

    return (
        <>
            <AdminOrdersColumnFilterPanel
                open={Boolean(openColumnFilter)}
                columnId={openColumnFilter}
                onClose={handleCloseColumnFilter}
                shippers={shippers}
                carriers={carriers}
                onwardAdminOptions={onwardAdminOptions}
                tab={tab}
                shipperIds={shipperIds}
                setShipperIds={setShipperIds}
                carrierIds={carrierIds}
                setCarrierIds={setCarrierIds}
                assignedAdminId={assignedAdminId}
                setAssignedAdminId={setAssignedAdminId}
                deliveryTypes={deliveryTypes}
                setDeliveryTypes={setDeliveryTypes}
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
                schedDelStart={schedDelStart}
                setSchedDelStart={setSchedDelStart}
                schedDelEnd={schedDelEnd}
                setSchedDelEnd={setSchedDelEnd}
                estShipStart={estShipStart}
                setEstShipStart={setEstShipStart}
                estShipEnd={estShipEnd}
                setEstShipEnd={setEstShipEnd}
                actualReceivedStart={actualReceivedStart}
                setActualReceivedStart={setActualReceivedStart}
                actualReceivedEnd={actualReceivedEnd}
                setActualReceivedEnd={setActualReceivedEnd}
                warehouseStartDate={warehouseStartDate}
                setWarehouseStartDate={setWarehouseStartDate}
                warehouseEndDate={warehouseEndDate}
                setWarehouseEndDate={setWarehouseEndDate}
                middleMileStatus={middleMileStatus}
                setMiddleMileStatus={setMiddleMileStatus}
                warehouseStatus={warehouseStatus}
                setWarehouseStatus={setWarehouseStatus}
                rereceivingStatus={rereceivingStatus}
                setRereceivingStatus={setRereceivingStatus}
                orderType={orderType}
                setOrderType={setOrderType}
                milestone={milestone}
                setMilestone={setMilestone}
                tiers={tiers}
                setTiers={setTiers}
                paymentType={paymentType}
                setPaymentType={setPaymentType}
                orderKinds={orderKinds}
                setOrderKinds={setOrderKinds}
            />
            <OuterContainer>
                <Snackbar
                    open={!!notification?.message}
                    onClose={() => setNotification({})}
                    anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
                    autoHideDuration={10000}
                >
                    <Alert
                        onClose={() => setNotification({})}
                        severity={notification?.severity}
                        elevation={6}
                        variant="filled"
                    >
                        {notification?.message}
                    </Alert>
                </Snackbar>
                <OrderPODModal
                    order={podOrder}
                    callbacks={{
                        onClose: () => {
                            viewOrderPod(null);
                        },
                    }}
                />
                <EditOrderModal
                    order={editingOrder}
                    open={isObject(editingOrder)}
                    callbacks={{
                        onSave,
                        onClose: () => editOrder(null),
                        onError: (error) =>
                            setNotification({
                                severity: 'error',
                                message: error?.message || error || 'Something went wrong.',
                            }),
                    }}
                    initTab={EDIT_ORDER_TABS.ADMIN}
                    opt={{
                        enableAdminTab: true,
                        enableAdminTestAccounts: test,
                    }}
                />
                <OnwardTabContainer
                    value={tab}
                    onChange={(e, tab) => {
                        setTab(tab);
                    }}
                    textColor="primary"
                    indicatorColor="primary"
                    css={css`
                        background-color: white;
                        & .MuiTabs-flexContainer {
                            justify-content: center;
                        }
                    `}
                >
                    {TABS.map((tab, idx) => (
                        <OnwardTab
                            key={`admin-orders-tab-${tab.value}`}
                            label={tab.label}
                            value={idx}
                            error={tab.red}
                        />
                    ))}
                </OnwardTabContainer>
                <Container
                    css={css`
                        height: calc(100vh - 150px);
                    `}
                >
                    <Row>
                        <Grid
                            direction="column"
                            container
                            css={css`
                                flex: 0;
                                flex-basis: 0;
                                margin-right: 12px;
                            `}
                        >
                            <div
                                css={css`
                                    display: flex;
                                    align-items: center;
                                    gap: 12px;
                                `}
                            >
                                <Title>Admin Orders</Title>
                                <Typography
                                    css={css`
                                        font-size: 14px;
                                        font-weight: 500;
                                        color: #4c4c4c;
                                        width: 150px;
                                    `}
                                >
                                    {isDistressedTab && !missingRequiredShipper
                                        ? `${displayedOrders.length} distressed / ${totalCount} total orders`
                                        : `${totalCount} total orders`}
                                </Typography>
                            </div>
                        </Grid>
                        <Grid
                            direction="row"
                            container
                            css={css`
                                flex: 1;
                                flex-basis: 0;
                                align-items: center;
                                justify-content: flex-end;
                                gap: 8px;
                            `}
                        >
                            <TextField
                                select
                                variant="outlined"
                                size="small"
                                label="Search Field"
                                value={searchField || ''}
                                onChange={(e) => {
                                    setSearchField(e.target.value);
                                }}
                                css={css`
                                    background-color: white;
                                    min-width: 180px;
                                `}
                            >
                                {SEARCH_OPTIONS.map((searchOption) => (
                                    <MenuItem key={searchOption.value} value={searchOption.value}>
                                        {searchOption.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                value={search}
                                placeholder="Search (OD# / PO# / Reference)"
                                onChange={handleSearch}
                                variant="outlined"
                                color="primary"
                                size="small"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search />
                                        </InputAdornment>
                                    ),
                                }}
                                css={css`
                                    background-color: white;
                                    max-width: 200px;
                                `}
                            />
                            <TextField
                                select
                                variant="outlined"
                                size="small"
                                label={profiles?.length ? 'Profile' : 'Select Profile...'}
                                InputLabelProps={{ shrink: true }}
                                {...profileSelectProps}
                                css={css`
                                    background-color: white;
                                    min-width: 220px;
                                `}
                            >
                                <MenuItem value="">Default</MenuItem>
                                {profiles.map((p) => (
                                    <MenuItem key={p.order_table_profile_id} value={p.order_table_profile_id}>
                                        {p.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <Button variant="contained" color="primary" onClick={() => setOpenAdminSettings(true)}>
                                <FilterList
                                    css={css`
                                        color: white;
                                    `}
                                />
                            </Button>
                        </Grid>
                    </Row>

                    <Row
                        css={css`
                            flex-grow: 1;
                            min-height: 400px;
                            flex-direction: column;
                            flex-wrap: nowrap;
                            ${(loading || submissionLoading) &&
                            `
                            pointer-events: none;
                            opacity: 0.7;
                        `}
                        `}
                    >
                        <AdminSettingsPanel
                            open={openAdminSettings}
                            onClose={() => setOpenAdminSettings(false)}
                            marketplace={marketplace}
                            setMarketplace={setMarketplace}
                            test={test}
                            setTest={setTest}
                            disabled={loading}
                        />
                        {(loading || fetchMoreLoading) && (
                            <LinearProgress
                                color="primary"
                                css={css`
                                    width: 100%;
                                `}
                            />
                        )}
                        {missingRequiredShipper ? (
                            <div
                                css={css`
                                    padding: 24px;
                                    display: flex;
                                    flex-direction: column;
                                    gap: 12px;
                                `}
                            >
                                <Typography variant="h6">Select shipper(s) to view distressed shipments</Typography>
                                <Typography variant="body2" style={{ maxWidth: 720 }}>
                                    Distressed shipments are computed client-side from the same milestone threshold
                                    logic used for row coloring. To keep this fast, please filter by shipper first.
                                </Typography>
                                <div>
                                    <PrimaryButton onClick={() => setOpenColumnFilter('shipper')}>
                                        Filter Shippers
                                    </PrimaryButton>
                                </div>
                            </div>
                        ) : (
                            <CustomizableTable
                                key={tableKey}
                                orders={displayedOrders}
                                columns={columns}
                                tab={TABS[tab]}
                                onRowClick={(order) => {
                                    switch (TABS[tab].value) {
                                        case 'INCOMPLETE':
                                            window.open(`/job/${order.job_id}`, '_blank');
                                            break;
                                        default:
                                            editOrder(order);
                                            break;
                                    }
                                }}
                                enableRowSelection={false}
                                sortBy={[sortBy]}
                                onSortChange={handleSortBy}
                                highlightRow={rowHighlightFn}
                                callbacks={{
                                    handleColor,
                                }}
                                onLoadMore={loadMoreOrders}
                                hasMore={hasMore}
                                isLoading={loading || fetchMoreLoading}
                                filterableColumns={filterableColumnIds}
                                onOpenColumnFilter={handleOpenColumnFilter}
                                onClearColumnFilter={handleClearColumnFilter}
                                initialState={tableInitialState}
                                onLayoutChange={setTableLayoutState}
                            />
                        )}
                    </Row>
                    <Grid
                        container
                        justifyContent="center"
                        alignItems="center"
                        css={css`
                            width: calc(100% + 6rem);
                            margin-left: -3rem;
                            margin-right: -3rem;
                            margin-bottom: -1.5rem;

                            box-shadow: 0px -4px 4px 0px #37373752;
                            min-height: 75px !important;
                            background-color: white;
                        `}
                    >
                        <div
                            css={css`
                                margin-left: 1rem;
                            `}
                        >
                            <CustomizableTableProfileControl
                                disabled={loading}
                                tableType="ADMIN_ORDERS"
                                currentProfileName={currentProfileName}
                                currentProfileId={currentProfileId}
                                selectedProfileId={selectedProfileId}
                                onSaveNew={handleSaveNewProfile}
                                onOverwrite={handleOverwriteProfile}
                                onImportById={handleImportProfileById}
                                onDeleteCurrent={handleDeleteCurrentProfile}
                                saving={insertProfileLoading}
                                overwriting={updateProfileLoading}
                                deleting={deleteProfileLoading}
                            />
                        </div>

                        <PrimaryButton
                            loading={csvInflight}
                            onClick={exportCSV}
                            disabled={missingRequiredShipper || isDistressedTab}
                            css={css`
                                margin-left: 1rem;
                                height: 36px;
                            `}
                        >
                            Export
                        </PrimaryButton>
                    </Grid>
                </Container>
            </OuterContainer>
        </>
    );
};

export default compose(withAdminRights)(AdminOrders);
